/**
 * BlogPage — Listado de artículos del blog (fixtures locales).
 *
 * No hay endpoint de blog en el backend todavía. Cuando se agregue, este
 * componente se conectará vía un hook useBlogPosts() similar a los otros.
 */

import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { Search, SearchX, User } from 'lucide-react';
import { cn } from '@shared/utils/cn';

type Category = 'Salud' | 'Razas' | 'Alimentación' | 'Comportamiento' | 'Cuidados';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  category: Category;
  author: string;
  publishedAt: string;
}

const ARTICLES: Article[] = [
  {
    id: '1',
    title: 'Cómo preparar a tu perro para una visita al veterinario',
    excerpt:
      'Reducir el estrés en la consulta veterinaria es clave para una atención exitosa. Te compartimos consejos prácticos para que tu mascota se sienta segura.',
    imageUrl: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=800&q=80',
    category: 'Cuidados',
    author: 'Dra. Camila Pérez',
    publishedAt: '2026-04-12',
  },
  {
    id: '2',
    title: '5 razas de perros ideales para departamento',
    excerpt:
      'Vivir en departamento no significa renunciar a tener un compañero. Descubre las razas más adaptadas a espacios pequeños.',
    imageUrl: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=800&q=80',
    category: 'Razas',
    author: 'Equipo mishiwoof',
    publishedAt: '2026-04-05',
  },
  {
    id: '3',
    title: 'Alimentación natural vs. balanceado: ¿qué es mejor?',
    excerpt:
      'El debate sobre la dieta ideal sigue abierto. Analizamos pros y contras de cada enfoque con respaldo veterinario.',
    imageUrl: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=800&q=80',
    category: 'Alimentación',
    author: 'Dra. Sofía Martínez',
    publishedAt: '2026-03-28',
  },
  {
    id: '4',
    title: 'Señales de estrés en gatos que estás pasando por alto',
    excerpt:
      'Los gatos son maestros en ocultar malestar. Aprende a leer las señales sutiles que indican que algo no anda bien.',
    imageUrl: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=800&q=80',
    category: 'Comportamiento',
    author: 'Etóloga Paula Soto',
    publishedAt: '2026-03-15',
  },
  {
    id: '5',
    title: 'Vacunas esenciales para perros y gatos en Chile',
    excerpt:
      'Calendario de vacunación recomendado según las patologías más frecuentes en nuestro país.',
    imageUrl: 'https://images.unsplash.com/photo-1581888227599-779811939961?w=800&q=80',
    category: 'Salud',
    author: 'Dr. Rodrigo Ávila',
    publishedAt: '2026-03-01',
  },
  {
    id: '6',
    title: 'Cómo socializar a tu cachorro de forma efectiva',
    excerpt:
      'Las primeras 16 semanas son decisivas. Descubre las técnicas que recomiendan los especialistas para una socialización exitosa.',
    imageUrl: 'https://images.unsplash.com/photo-1546238232-20216dec9f72?w=800&q=80',
    category: 'Comportamiento',
    author: 'Adiestrador Cristian López',
    publishedAt: '2026-02-20',
  },
];

const CATEGORIES: (Category | 'Todas')[] = [
  'Todas',
  'Salud',
  'Razas',
  'Alimentación',
  'Comportamiento',
  'Cuidados',
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function BlogPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<Category | 'Todas'>('Todas');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ARTICLES.filter((a) => {
      if (category !== 'Todas' && a.category !== category) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.author.toLowerCase().includes(q)
      );
    });
  }, [search, category]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Blog mishiwoof"
        subtitle="Consejos, guías y novedades para cuidar a tu peludo"
      />

      {/* Buscador */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar artículos…"
          className="pl-9"
        />
      </div>

      {/* Chips categoría */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            type="button"
            key={c}
            onClick={() => setCategory(c)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              category === c
                ? 'border-sidebar-primary/30 bg-sidebar-primary/15 text-sidebar-primary'
                : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title="Sin resultados"
          description="No encontramos artículos para esa búsqueda. Probá con otras palabras o cambiá la categoría."
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <article
              key={a.id}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-xs transition-shadow hover:shadow-md"
            >
              <div className="relative aspect-video w-full overflow-hidden bg-muted">
                <img
                  src={a.imageUrl}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-sidebar-primary px-2.5 py-0.5 text-xs font-medium text-sidebar-primary-foreground">
                  {a.category}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-5">
                <h3 className="text-base font-semibold leading-snug text-foreground">
                  {a.title}
                </h3>
                <p className="line-clamp-3 text-sm text-muted-foreground">{a.excerpt}</p>
                <div className="mt-auto flex items-center gap-2 pt-3 text-xs text-muted-foreground">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground">
                    <User className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <span className="font-medium text-foreground">{a.author}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatDate(a.publishedAt)}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Más artículos en camino. Suscríbete a nuestras notificaciones para recibirlos primero.
      </p>
    </div>
  );
}
